import api, { unwrapList, unwrapOne } from "./apiClient";
import { BACKEND_URL } from "@/config/api";
import { formatCurrency } from "@/lib/format";

const API_BASE = `${BACKEND_URL}/frete`;
const RATES_API = `${API_BASE}/taxas`;

const byMinDistance = (a, b) =>
  Number(a.minDistanceKm) - Number(b.minDistanceKm);

/**
 * Freight Service
 * Taxas de frete por distância (FreightRateController - /frete/taxas) e
 * cálculo de frete por CEP (FreightController - /frete/{cep}/calcular).
 * Este recurso usa camelCase e responde sem o envelope { data }.
 * A distância é em raio linear (Haversine) a partir do CEP da empresa.
 */
class FreightService {
  /**
   * Listar taxas de frete ativas, ordenadas pela distância mínima
   * @returns {Promise<Array>} [{ id, minDistanceKm, maxDistanceKm, freightValue, active }]
   */
  async getAllRates() {
    try {
      const response = await api.get(RATES_API);
      return [...unwrapList(response)].sort(byMinDistance);
    } catch (error) {
      console.error("❌ Erro ao buscar taxas de frete:", error);
      throw error;
    }
  }

  /**
   * Buscar taxa por ID
   * @param {number} id
   * @returns {Promise<object>}
   */
  async getRateById(id) {
    try {
      const response = await api.get(`${RATES_API}/${id}`);
      return unwrapOne(response);
    } catch (error) {
      console.error(`❌ Erro ao buscar taxa de frete ${id}:`, error);
      throw error;
    }
  }

  /**
   * Criar nova taxa de frete
   * @param {object} rateData - { minDistanceKm, maxDistanceKm, freightValue, active }
   * @returns {Promise<object>}
   */
  async createRate(rateData) {
    try {
      const response = await api.post(RATES_API, rateData);
      return unwrapOne(response);
    } catch (error) {
      console.error("❌ Erro ao criar taxa de frete:", error);
      throw error;
    }
  }

  /**
   * Atualizar taxa de frete
   * @param {number} id
   * @param {object} rateData - { minDistanceKm, maxDistanceKm, freightValue, active }
   * @returns {Promise<object>}
   */
  async updateRate(id, rateData) {
    try {
      const response = await api.put(`${RATES_API}/${id}`, rateData);
      return unwrapOne(response);
    } catch (error) {
      console.error(`❌ Erro ao atualizar taxa de frete ${id}:`, error);
      throw error;
    }
  }

  /**
   * Desativar taxa de frete (o backend não apaga o registro, só marca active=false)
   * @param {number} id
   * @returns {Promise<void>}
   */
  async deleteRate(id) {
    try {
      await api.delete(`${RATES_API}/${id}`);
    } catch (error) {
      console.error(`❌ Erro ao desativar taxa de frete ${id}:`, error);
      throw error;
    }
  }

  /**
   * Calcular frete para o CEP de destino
   * @param {string} cep - com ou sem máscara
   * @returns {Promise<object>} { originCep, destinationCep, distanceKm, freightValue, message, available }
   */
  async calculateFreight(cep) {
    try {
      const cleanCep = this.cleanCep(cep);
      const response = await api.get(`${API_BASE}/${cleanCep}/calcular`);
      return unwrapOne(response);
    } catch (error) {
      console.error(`❌ Erro ao calcular frete para ${cep}:`, error);
      throw error;
    }
  }

  /**
   * Remove a máscara do CEP
   * @param {string} cep
   * @returns {string}
   */
  cleanCep(cep) {
    return String(cep || "").replace(/\D/g, "");
  }

  /**
   * Aplica a máscara 00000-000 enquanto o usuário digita
   * @param {string} cep
   * @returns {string}
   */
  formatCep(cep) {
    const digits = this.cleanCep(cep).slice(0, 8);
    return digits.length > 5
      ? `${digits.slice(0, 5)}-${digits.slice(5)}`
      : digits;
  }

  /**
   * Converte o texto digitado ("5,5", "12,00", "1.234,50", "5.5") em número
   * @param {string|number} value
   * @returns {number} NaN quando vazio ou inválido
   */
  parseNumber(value) {
    if (typeof value === "number") return value;
    const text = String(value ?? "").trim();
    if (!text) return NaN;
    const normalized = text.includes(",")
      ? text.replace(/\./g, "").replace(",", ".")
      : text;
    return Number(normalized);
  }

  /**
   * Formata valor em reais
   * @param {number} value
   * @returns {string}
   */
  formatCurrency(value) {
    return formatCurrency(value);
  }

  /**
   * Número de km sem a unidade ("5,5")
   * @param {number} value
   * @returns {string}
   */
  formatKm(value) {
    const number = Number(value);
    if (!Number.isFinite(number)) return "-";
    return number.toLocaleString("pt-BR", { maximumFractionDigits: 2 });
  }

  /**
   * Formata distância em km ("5,5 km")
   * @param {number} value
   * @returns {string}
   */
  formatDistance(value) {
    const km = this.formatKm(value);
    return km === "-" ? km : `${km} km`;
  }

  /**
   * Faixa de distância ("2 km – 3 km")
   * @param {object} rate - { minDistanceKm, maxDistanceKm }
   * @returns {string}
   */
  formatRange(rate) {
    return `${this.formatDistance(rate?.minDistanceKm)} – ${this.formatDistance(rate?.maxDistanceKm)}`;
  }

  /**
   * Valida a taxa com as regras de faixa usadas no cálculo
   * (distância >= mínima e < máxima)
   * @param {object} data - valores do formulário
   * @returns {object} erros por campo
   */
  validateRateData(data) {
    const errors = {};
    const min = this.parseNumber(data.minDistanceKm);
    const max = this.parseNumber(data.maxDistanceKm);
    const value = this.parseNumber(data.freightValue);

    if (!Number.isFinite(min) || min < 0) {
      errors.minDistanceKm = "Informe uma distância mínima maior ou igual a 0";
    }
    if (!Number.isFinite(max)) {
      errors.maxDistanceKm = "Informe a distância máxima";
    } else if (Number.isFinite(min) && max <= min) {
      errors.maxDistanceKm = "A distância máxima deve ser maior que a mínima";
    }
    if (!Number.isFinite(value) || value < 0) {
      errors.freightValue = "Informe um valor maior ou igual a 0";
    }

    return errors;
  }

  /**
   * Faixas ativas que se sobrepõem à faixa informada (ignora a própria taxa)
   * @param {object} rate - { id, minDistanceKm, maxDistanceKm }
   * @param {Array} rates - taxas ativas
   * @returns {Array}
   */
  findOverlaps(rate, rates) {
    const min = Number(rate.minDistanceKm);
    const max = Number(rate.maxDistanceKm);
    return rates.filter(
      (other) =>
        other.id !== rate.id &&
        min < Number(other.maxDistanceKm) &&
        Number(other.minDistanceKm) < max,
    );
  }

  /**
   * Intervalos de distância sem taxa entre 0 km e a maior distância cadastrada
   * @param {Array} rates - taxas ativas
   * @returns {Array<{from: number, to: number}>}
   */
  findGaps(rates) {
    const gaps = [];
    let coveredUntil = 0;
    [...rates].sort(byMinDistance).forEach((rate) => {
      const min = Number(rate.minDistanceKm);
      const max = Number(rate.maxDistanceKm);
      if (min > coveredUntil) {
        gaps.push({ from: coveredUntil, to: min });
      }
      coveredUntil = Math.max(coveredUntil, max);
    });
    return gaps;
  }

  /**
   * Resumo das faixas para os cards de métrica
   * @param {Array} rates - taxas ativas
   * @returns {{ count: number, coverage: number, min: number, max: number, trend: string }}
   *   trend: "única" (mesmo valor), "gradual" (não diminui com a distância) ou "variável"
   */
  getSummary(rates) {
    const sorted = [...rates].sort(byMinDistance);
    const values = sorted.map((rate) => Number(rate.freightValue) || 0);
    const coverage = sorted.reduce(
      (max, rate) => Math.max(max, Number(rate.maxDistanceKm) || 0),
      0,
    );
    const min = values.length ? Math.min(...values) : 0;
    const max = values.length ? Math.max(...values) : 0;
    const gradual = values.every((value, i) => i === 0 || value >= values[i - 1]);

    return {
      count: sorted.length,
      coverage,
      min,
      max,
      trend: min === max ? "única" : gradual ? "gradual" : "variável",
    };
  }
}

// Export singleton instance
const freightService = new FreightService();
export default freightService;
